<template>
  <q-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card style="width: 540px; max-width: 95vw" class="manage-ps-dialog">
      <q-toolbar class="bg-blue-brand text-white">
        <q-toolbar-title>Pipeline Manager</q-toolbar-title>
        <q-btn v-close-popup flat round dense icon="close" />
      </q-toolbar>

      <q-tabs
        v-model="tab"
        dense
        class="text-grey"
        active-color="blue-brand"
        indicator-color="blue-brand"
        align="left"
        update-csv-file
        narrow-indicator
      >
        <q-tab name="create" label="Create New" />
        <q-tab name="update" label="Update Existing" />
      </q-tabs>

      <q-separator />

      <q-tab-panels v-model="tab" animated keep-alive>
        <!-- Create New Pipeline Section -->
        <q-tab-panel name="create">
          <div class="q-gutter-md" style="max-width: 500px">
            <q-input
              v-model="createName"
              dense
              outlined
              color="blue-brand"
              label="Name e.g., Oklahoma City to Tulsa"
              :error="createNameError !== ''"
              :error-message="createNameError"
            />

            <div class="row items-start q-gutter-sm">
              <q-input
                v-model="createDiameter"
                dense
                outlined
                color="blue-brand"
                :label="
                  'Diameter (' +
                  (createDiameterUnit === 'in' ? 'inches' : 'cm') +
                  ')'
                "
                :error="createDiameterError !== ''"
                :error-message="createDiameterError"
                :rules="[createDiameterRule]"
                reactive-rules
                class="col"
              />
              <q-select
                v-model="createDiameterUnit"
                dense
                outlined
                color="blue-brand"
                :options="[
                  { label: 'in', value: 'in' },
                  { label: 'cm', value: 'cm' },
                ]"
                emit-value
                map-options
                style="width: 90px"
              />
            </div>

            <q-file
              v-model="createCsvFile"
              dense
              outlined
              color="blue-brand"
              label="Survey Markers and Tracking Locations (CSV) File Upload"
              accept=".csv"
              clearable
              :error="createCsvFileError !== ''"
              :error-message="createCsvFileError"
            >
              <template #prepend>
                <q-icon name="attach_file" />
              </template>
            </q-file>

            <div>
              <q-btn
                class="btn-brand"
                label="Create"
                :loading="loading"
                @click="onCreateSubmit"
              />
            </div>

            <!-- Mini map preview for create -->
            <div v-show="createCsvFile" class="q-mt-md">
              <div class="text-caption text-grey-8 q-mb-xs">
                Pipeline Preview
              </div>
              <div
                ref="createMiniMap"
                style="
                  height: 250px;
                  width: 100%;
                  border: 1px solid #ccc;
                  border-radius: 4px;
                "
              />
            </div>
          </div>
        </q-tab-panel>

        <!-- Update Existing Pipeline Section -->
        <q-tab-panel name="update">
          <div class="q-gutter-md" style="max-width: 500px">
            <q-select
              v-model="selectedPipelineSection"
              dense
              outlined
              color="blue-brand"
              label="Name"
              :options="pipelineSectionOptions"
              :loading="loading"
              clearable
              @update:model-value="onPipelineSectionSelected"
              @clear="onPipelineSectionCleared"
            />

            <div class="row items-start q-gutter-sm">
              <q-input
                v-model="updateDiameter"
                dense
                outlined
                color="blue-brand"
                :label="
                  'Diameter (' +
                  (updateDiameterUnit === 'in' ? 'inches' : 'cm') +
                  ')'
                "
                :disable="!selectedPipelineSection"
                :rules="[(val) => !!val || 'Diameter is required.']"
                class="col"
              />
              <q-select
                v-model="updateDiameterUnit"
                dense
                outlined
                color="blue-brand"
                :options="[
                  { label: 'in', value: 'in' },
                  { label: 'cm', value: 'cm' },
                ]"
                emit-value
                map-options
                :disable="!selectedPipelineSection"
                style="width: 90px"
              />
            </div>

            <!-- Current tracking file -->
            <div v-if="selectedPipelineSection && currentTrackingFileName">
              <div class="text-caption text-grey-8 q-mb-xs">
                Current Tracking File
              </div>
              <div class="row items-center q-gutter-sm">
                <span class="text-body2">{{ currentTrackingFileName }}</span>
                <q-btn
                  flat
                  dense
                  size="sm"
                  icon="visibility"
                  color="blue-brand"
                  title="View file"
                  @click="onViewTrackingFile"
                />
                <q-btn
                  flat
                  dense
                  size="sm"
                  icon="download"
                  color="blue-brand"
                  title="Download file"
                  @click="onDownloadTrackingFile"
                />
              </div>
            </div>

            <q-file
              v-model="updateCsvFile"
              dense
              outlined
              color="blue-brand"
              label="Survey Markers and Tracking Locations (CSV) File Upload"
              accept=".csv"
              clearable
              :disable="!selectedPipelineSection"
            >
              <template #prepend>
                <q-icon name="attach_file" />
              </template>
            </q-file>

            <div class="row q-gutter-sm">
              <q-btn
                class="btn-brand"
                label="Update"
                :disable="!selectedPipelineSection"
                :loading="loading"
                @click="onUpdateSubmit"
              />
              <q-btn
                color="negative"
                label="Delete"
                :disable="!selectedPipelineSection"
                :loading="loading"
                @click="onDeleteSubmit"
              />
            </div>

            <!-- Mini map preview -->
            <div
              v-show="selectedPipelineSection && currentTrackingFile"
              class="q-mt-md"
            >
              <div class="text-caption text-grey-8 q-mb-xs">
                Pipeline Preview
              </div>
              <div
                ref="miniMap"
                style="
                  height: 250px;
                  width: 100%;
                  border: 1px solid #ccc;
                  border-radius: 4px;
                "
              />
            </div>
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </q-card>

    <!-- View Tracking File Dialog -->
    <q-dialog
      v-model="viewTrackingFileDialog"
      maximized
      @hide="onViewTrackingFileDialogHide"
    >
      <q-card>
        <q-toolbar class="bg-blue-brand text-white">
          <q-toolbar-title>{{ currentTrackingFileName }}</q-toolbar-title>
          <q-btn
            flat
            round
            dense
            icon="download"
            title="Download"
            @click="onDownloadTrackingFile"
          />
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section
          class="q-pa-md"
          style="overflow: auto; max-height: calc(100vh - 50px)"
        >
          <pre
            style="
              white-space: pre-wrap;
              word-break: break-all;
              font-size: 13px;
            "
            >{{ currentTrackingFile }}</pre
          >
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-dialog>

  <InstructionCard
    v-model="showInstructions"
    title="Pipelines"
    icon="description"
    message="Create a pipeline by entering a name, diameter, and uploading a CSV file with your marker positions."
    tip="Download an example CSV to see the expected format."
    tip-action-label="Download example CSV"
    :z-index="7000"
    @tip-action="onDownloadExampleCsv"
  />
</template>

<script lang="ts">
import L from 'leaflet';
import { defineComponent, markRaw } from 'vue';
import PipelineSectionService from '@/services/PipelineSectionService';
import InstructionCard from '@/components/InstructionCard.vue';
import { CreatePipelineRequest } from '@/models/PipelineSection/CreatePipelineRequest';
import { UpdatePipelineRequest } from '@/models/PipelineSection/UpdatePipelineRequest';
import { PipelineSection } from '@/models/PipelineSection/PipelineSection';

export default defineComponent({
  name: 'PipelineSectionManager',
  components: { InstructionCard },
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'sections-updated'],
  data() {
    return {
      tab: 'create',
      loading: false,

      // Create form state
      createName: '',
      createNameError: '',
      createDiameter: '',
      createDiameterError: '',
      createDiameterUnit: 'in' as 'in' | 'cm',
      createCsvFile: null as File | null,
      createCsvFileError: '',

      // Update form state
      selectedPipelineSection: null as any,
      updateDiameter: '',
      updateDiameterUnit: 'in' as 'in' | 'cm',
      updateCsvFile: null as File | null,
      currentTrackingFileName: '',
      currentTrackingFile: '',
      viewTrackingFileDialog: false,

      // Full pipeline section data from API
      pipelineSections: [] as PipelineSection[],

      // Map preview markers for CSV points (main map, used by view dialog)
      csvMapMarkers: [] as L.Marker[],
      csvPolyline: null as L.Polyline | null,

      // Mini map for update tab
      miniMap: null as L.Map | null,
      miniMapLayer: null as L.LayerGroup | null,

      // Mini map for create tab
      createMiniMap: null as L.Map | null,
      createMiniMapLayer: null as L.LayerGroup | null,

      // Dropdown options populated from API
      pipelineSectionOptions: [] as any[],
      showInstructions: false,
    };
  },
  computed: {
    map(): any {
      return this.$store.state.map;
    },
  },
  watch: {
    modelValue(val: boolean) {
      if (val) {
        this.showInstructions = true;
        this.resetForm();
        this.loadPipelineSections();
      } else {
        this.showInstructions = false;
        this.destroyMiniMap();
        this.destroyCreateMiniMap();
      }
    },
    createCsvFile(file: File | null) {
      if (!file) {
        this.clearCreateMiniMap();
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (content) {
          this.plotCsvPointsOnCreateMiniMap(content);
        }
      };
      reader.readAsText(file);
    },
    updateDiameterUnit(newVal: 'in' | 'cm', oldVal: 'in' | 'cm') {
      const val = parseFloat(this.updateDiameter);
      if (!isNaN(val) && val > 0) {
        const meters = oldVal === 'in' ? val * 0.0254 : val * 0.01;
        const converted = newVal === 'in' ? meters / 0.0254 : meters / 0.01;
        this.updateDiameter = String(parseFloat(converted.toFixed(4)));
      }
    },
    updateCsvFile(file: File | null) {
      if (!file) {
        if (this.currentTrackingFile) {
          this.$nextTick(() => {
            this.plotCsvPointsOnMiniMap(this.currentTrackingFile);
          });
        } else {
          this.clearMiniMap();
        }
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (content) {
          this.plotCsvPointsOnMiniMap(content);
        }
      };
      reader.readAsText(file);
    },
  },
  methods: {
    onDownloadExampleCsv() {
      const link = document.createElement('a');
      link.href = `${process.env.BASE_URL || '/'}example.csv`;
      link.download = 'example.csv';
      link.click();
    },
    createDiameterRule(val: string): boolean | string {
      if (!val || val.trim().length === 0) return true;
      const num = parseFloat(val);
      if (isNaN(num) || num <= 0) return 'Diameter must be a positive number.';
      const meters =
        this.createDiameterUnit === 'in' ? num * 0.0254 : num * 0.01;
      if (meters < 0.1016 || meters > 1.27) {
        return this.createDiameterUnit === 'in'
          ? 'Diameter must be between 4 and 50 inches.'
          : 'Diameter must be between 10.16 and 127 cm.';
      }
      return true;
    },

    resetForm() {
      this.tab = 'create';
      this.createName = '';
      this.createNameError = '';
      this.createDiameter = '';
      this.createDiameterError = '';
      this.createDiameterUnit = 'in';
      this.createCsvFile = null;
      this.createCsvFileError = '';
      this.selectedPipelineSection = null;
      this.updateDiameter = '';
      this.updateDiameterUnit = 'in';
      this.updateCsvFile = null;
      this.currentTrackingFileName = '';
      this.currentTrackingFile = '';
      this.viewTrackingFileDialog = false;
    },

    loadPipelineSections() {
      this.loading = true;

      PipelineSectionService.getPipelineSections()
        .then((response: any) => {
          this.pipelineSections = response.data.map(
            (ps: any) =>
              new PipelineSection(
                ps.pipelineSectionId,
                ps.name,
                ps.diameter,
                ps.trackingFileName,
                ps.trackingFile
              )
          );
          this.pipelineSectionOptions = this.pipelineSections
            .map((ps: PipelineSection) => ({
              label: ps.name,
              value: ps.pipelineSectionId,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label));
        })
        .catch((err: any) => {
          console.error('Failed to load pipeline sections:', err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onPipelineSectionSelected(val: any) {
      if (!val) {
        return;
      }
      const section = this.pipelineSections.find(
        (ps) => ps.pipelineSectionId === val.value
      );
      if (section) {
        if (section.diameter != null) {
          const converted =
            this.updateDiameterUnit === 'in'
              ? section.diameter / 0.0254
              : section.diameter / 0.01;
          this.updateDiameter = String(parseFloat(converted.toFixed(4)));
        } else {
          this.updateDiameter = '';
        }
        this.currentTrackingFileName = section.trackingFileName || '';
        this.currentTrackingFile = section.trackingFile || '';
        this.$nextTick(() => {
          this.plotCsvPointsOnMiniMap(this.currentTrackingFile);
        });
      }
    },

    onPipelineSectionCleared() {
      this.updateDiameter = '';
      this.updateDiameterUnit = 'in';
      this.updateCsvFile = null;
      this.currentTrackingFileName = '';
      this.currentTrackingFile = '';
      this.clearMiniMap();
    },

    onViewTrackingFile() {
      if (this.currentTrackingFile) {
        this.viewTrackingFileDialog = true;
        this.plotCsvPointsOnMap(this.currentTrackingFile);
      }
    },

    onViewTrackingFileDialogHide() {
      this.clearCsvMapMarkers();
    },

    plotCsvPointsOnMap(csv: string) {
      this.clearCsvMapMarkers();
      if (!this.map || !csv) return;

      const lines = csv
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);
      if (lines.length < 2) return;

      const header = lines[0]
        .split(',')
        .map((h: string) => h.trim().toLowerCase());
      const latIdx = header.indexOf('latitude');
      const lonIdx = header.indexOf('longitude');
      const nameIdx = header.indexOf('agm marker name');
      if (latIdx === -1 || lonIdx === -1) return;

      const latLngs: L.LatLng[] = [];

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map((c: string) => c.trim());
        const lat = parseFloat(cols[latIdx]);
        const lon = parseFloat(cols[lonIdx]);
        if (isNaN(lat) || isNaN(lon)) continue;

        const name = nameIdx !== -1 ? cols[nameIdx] : `Point ${i}`;
        const latlng = L.latLng(lat, lon);
        latLngs.push(latlng);

        const marker = L.marker(latlng).bindTooltip(name).addTo(this.map);
        this.csvMapMarkers.push(markRaw(marker));
      }

      if (latLngs.length > 1) {
        this.csvPolyline = markRaw(
          L.polyline(latLngs, {
            color: '#1976D2',
            weight: 2,
            dashArray: '6 4',
          }).addTo(this.map)
        );
        this.map.fitBounds(this.csvPolyline.getBounds());
      } else if (latLngs.length === 1) {
        this.map.setView(latLngs[0], 14);
      }
    },

    clearCsvMapMarkers() {
      for (const marker of this.csvMapMarkers) {
        marker.removeFrom(this.map);
      }
      this.csvMapMarkers = [];
      if (this.csvPolyline) {
        this.csvPolyline.removeFrom(this.map);
        this.csvPolyline = null;
      }
    },

    initMiniMap() {
      if (this.miniMap) return;
      const el = this.$refs.miniMap as HTMLElement;
      if (!el) return;

      this.miniMap = markRaw(
        L.map(el, {
          zoomControl: true,
          attributionControl: false,
        }).setView([0, 0], 2)
      );

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(this.miniMap as L.Map);

      this.miniMapLayer = markRaw(L.layerGroup().addTo(this.miniMap as L.Map));
    },

    clearMiniMap() {
      if (this.miniMapLayer) {
        this.miniMapLayer.clearLayers();
      }
    },

    destroyMiniMap() {
      if (this.miniMap) {
        this.miniMap.remove();
        this.miniMap = null;
        this.miniMapLayer = null;
      }
    },

    initCreateMiniMap() {
      if (this.createMiniMap) return;
      const el = this.$refs.createMiniMap as HTMLElement;
      if (!el) return;

      this.createMiniMap = markRaw(
        L.map(el, {
          zoomControl: true,
          attributionControl: false,
        }).setView([0, 0], 2)
      );

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(this.createMiniMap as L.Map);

      this.createMiniMapLayer = markRaw(
        L.layerGroup().addTo(this.createMiniMap as L.Map)
      );
    },

    clearCreateMiniMap() {
      if (this.createMiniMapLayer) {
        this.createMiniMapLayer.clearLayers();
      }
    },

    destroyCreateMiniMap() {
      if (this.createMiniMap) {
        this.createMiniMap.remove();
        this.createMiniMap = null;
        this.createMiniMapLayer = null;
      }
    },

    plotCsvPointsOnCreateMiniMap(csv: string) {
      this.initCreateMiniMap();
      this.clearCreateMiniMap();
      if (!this.createMiniMap || !this.createMiniMapLayer || !csv) return;

      const lines = csv
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);
      if (lines.length < 2) return;

      const header = lines[0]
        .split(',')
        .map((h: string) => h.trim().toLowerCase());
      const latIdx = header.indexOf('latitude');
      const lonIdx = header.indexOf('longitude');
      const nameIdx = header.indexOf('agm marker name');
      if (latIdx === -1 || lonIdx === -1) return;

      const latLngs: L.LatLng[] = [];

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map((c: string) => c.trim());
        const lat = parseFloat(cols[latIdx]);
        const lon = parseFloat(cols[lonIdx]);
        if (isNaN(lat) || isNaN(lon)) continue;

        const name = nameIdx !== -1 ? cols[nameIdx] : `Point ${i}`;
        const latlng = L.latLng(lat, lon);
        latLngs.push(latlng);

        L.marker(latlng)
          .bindTooltip(name)
          .addTo(this.createMiniMapLayer as L.LayerGroup);
      }

      if (latLngs.length > 1) {
        L.polyline(latLngs, {
          color: '#1976D2',
          weight: 2,
          dashArray: '6 4',
        }).addTo(this.createMiniMapLayer as L.LayerGroup);
      }

      setTimeout(() => {
        if (!this.createMiniMap) return;
        this.createMiniMap.invalidateSize();
        if (latLngs.length > 1) {
          this.createMiniMap.fitBounds(L.latLngBounds(latLngs), {
            padding: [20, 20],
          });
        } else if (latLngs.length === 1) {
          this.createMiniMap.setView(latLngs[0], 14);
        }
      }, 100);
    },

    plotCsvPointsOnMiniMap(csv: string) {
      this.initMiniMap();
      this.clearMiniMap();
      if (!this.miniMap || !this.miniMapLayer || !csv) return;

      const lines = csv
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);
      if (lines.length < 2) return;

      const header = lines[0]
        .split(',')
        .map((h: string) => h.trim().toLowerCase());
      const latIdx = header.indexOf('latitude');
      const lonIdx = header.indexOf('longitude');
      const nameIdx = header.indexOf('agm marker name');
      if (latIdx === -1 || lonIdx === -1) return;

      const latLngs: L.LatLng[] = [];

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map((c: string) => c.trim());
        const lat = parseFloat(cols[latIdx]);
        const lon = parseFloat(cols[lonIdx]);
        if (isNaN(lat) || isNaN(lon)) continue;

        const name = nameIdx !== -1 ? cols[nameIdx] : `Point ${i}`;
        const latlng = L.latLng(lat, lon);
        latLngs.push(latlng);

        L.marker(latlng)
          .bindTooltip(name)
          .addTo(this.miniMapLayer as L.LayerGroup);
      }

      if (latLngs.length > 1) {
        L.polyline(latLngs, {
          color: '#1976D2',
          weight: 2,
          dashArray: '6 4',
        }).addTo(this.miniMapLayer as L.LayerGroup);
      }

      setTimeout(() => {
        if (!this.miniMap) return;
        this.miniMap.invalidateSize();
        if (latLngs.length > 1) {
          this.miniMap.fitBounds(L.latLngBounds(latLngs), {
            padding: [20, 20],
          });
        } else if (latLngs.length === 1) {
          this.miniMap.setView(latLngs[0], 14);
        }
      }, 100);
    },

    onDownloadTrackingFile() {
      if (!this.currentTrackingFile || !this.currentTrackingFileName) {
        return;
      }
      const blob = new Blob([this.currentTrackingFile], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', this.currentTrackingFileName);
      link.click();
      URL.revokeObjectURL(url);
    },

    onCreateSubmit() {
      this.createNameError = '';
      this.createDiameterError = '';
      this.createCsvFileError = '';

      const name = this.createName;
      if (!name || name.trim().length === 0) {
        this.createNameError = 'Name is required.';
      } else if (name.trim().length > 500) {
        this.createNameError = 'Name must be 500 characters or less.';
      }

      const diameter = this.createDiameter;
      const MIN_DIAMETER_M = 0.1016;
      const MAX_DIAMETER_M = 1.27;
      let diameterInMeters = 0;
      if (diameter === null || diameter === '') {
        this.createDiameterError = 'Diameter is required.';
      } else if (isNaN(parseFloat(diameter)) || parseFloat(diameter) <= 0) {
        this.createDiameterError = 'Diameter must be a positive number.';
      } else {
        diameterInMeters =
          this.createDiameterUnit === 'in'
            ? parseFloat(diameter) * 0.0254
            : parseFloat(diameter) * 0.01;
        if (
          diameterInMeters < MIN_DIAMETER_M ||
          diameterInMeters > MAX_DIAMETER_M
        ) {
          this.createDiameterError =
            this.createDiameterUnit === 'in'
              ? 'Diameter must be between 4 and 50 inches.'
              : 'Diameter must be between 10.16 and 127 cm.';
        }
      }

      const file = this.createCsvFile;
      if (!file) {
        this.createCsvFileError = 'CSV file is required.';
      } else if (!file.name || file.name.trim().length === 0) {
        this.createCsvFileError = 'File name must not be empty.';
      } else if (file.name.trim().length > 500) {
        this.createCsvFileError = 'File name must be 500 characters or less.';
      } else if (file.size === 0) {
        this.createCsvFileError = 'File must not be empty.';
      }

      if (
        this.createNameError ||
        this.createDiameterError ||
        this.createCsvFileError
      ) {
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const fileContent = e.target?.result as string;
        const diameterM =
          this.createDiameterUnit === 'in'
            ? parseFloat(this.createDiameter) * 0.0254
            : parseFloat(this.createDiameter) * 0.01;
        const request = new CreatePipelineRequest(
          this.createName.trim(),
          diameterM,
          this.createCsvFile!.name.trim(),
          fileContent
        );
        PipelineSectionService.createPipelineSection(request)
          .then(() => {
            this.$q.notify({
              color: 'positive',
              message: 'Pipeline created.',
            });
            this.$emit('update:modelValue', false);
            this.refreshAndEmit();
          })
          .catch((error: any) => {
            console.error('Failed to create pipeline:', error);
            const msg =
              error?.response?.status === 400 && error?.response?.data
                ? String(error.response.data)
                : 'Failed to create pipeline.';
            this.createNameError = error?.response?.status === 400 ? msg : '';
            if (error?.response?.status !== 400) {
              this.$q.notify({ color: 'negative', message: msg });
            }
          });
      };
      reader.readAsText(this.createCsvFile!);
    },

    onUpdateSubmit() {
      if (!this.selectedPipelineSection || !this.updateDiameter) {
        this.$q.notify({
          color: 'warning',
          message: 'Name and Diameter are required.',
        });
        return;
      }

      const MIN_DIAMETER_M = 0.1016;
      const MAX_DIAMETER_M = 1.27;
      const updateDiameterM =
        this.updateDiameterUnit === 'in'
          ? parseFloat(this.updateDiameter) * 0.0254
          : parseFloat(this.updateDiameter) * 0.01;
      if (
        isNaN(updateDiameterM) ||
        updateDiameterM < MIN_DIAMETER_M ||
        updateDiameterM > MAX_DIAMETER_M
      ) {
        this.$q.notify({
          color: 'warning',
          message:
            this.updateDiameterUnit === 'in'
              ? 'Diameter must be between 4 and 50 inches.'
              : 'Diameter must be between 10.16 and 127 cm.',
        });
        return;
      }

      this.loading = true;

      const sendUpdate = (
        trackingFileName: string | null,
        trackingFile: string | null
      ) => {
        const diameterM =
          this.updateDiameterUnit === 'in'
            ? parseFloat(this.updateDiameter) * 0.0254
            : parseFloat(this.updateDiameter) * 0.01;
        const request = new UpdatePipelineRequest(
          this.selectedPipelineSection.value,
          diameterM,
          trackingFileName,
          trackingFile
        );
        PipelineSectionService.updatePipelineSection(request)
          .then(() => {
            this.$q.notify({
              color: 'positive',
              message: 'Pipeline updated.',
            });
            this.$emit('update:modelValue', false);
            this.refreshAndEmit();
          })
          .catch((error: any) => {
            console.error('Failed to update pipeline:', error);
            this.$q.notify({
              color: 'negative',
              message: 'Failed to update pipeline.',
            });
          })
          .finally(() => {
            this.loading = false;
          });
      };

      if (this.updateCsvFile) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const fileContent = e.target?.result as string;
          sendUpdate(this.updateCsvFile!.name.trim(), fileContent);
        };
        reader.readAsText(this.updateCsvFile);
      } else {
        sendUpdate(null, null);
      }
    },

    onDeleteSubmit() {
      if (!this.selectedPipelineSection) {
        this.$q.notify({
          color: 'warning',
          message: 'Please select a pipeline section to delete.',
        });
        return;
      }

      this.$q
        .dialog({
          title: 'Confirm Delete',
          message: `Are you sure you want to delete "${this.selectedPipelineSection.label}"? This action cannot be undone.`,
          cancel: true,
          persistent: true,
        })
        .onOk(() => {
          this.loading = true;
          PipelineSectionService.deletePipelineSection(
            this.selectedPipelineSection.value
          )
            .then(() => {
              this.$q.notify({
                color: 'positive',
                message: 'Pipeline deleted.',
              });
              this.selectedPipelineSection = null;
              this.updateDiameter = '';
              this.currentTrackingFileName = '';
              this.currentTrackingFile = '';
              this.refreshAndEmit();
            })
            .catch((error: any) => {
              console.error('Failed to delete pipeline:', error);
              this.$q.notify({
                color: 'negative',
                message: 'Failed to delete pipeline.',
              });
            })
            .finally(() => {
              this.loading = false;
            });
        });
    },

    refreshAndEmit() {
      PipelineSectionService.getPipelineSections()
        .then((response: any) => {
          this.pipelineSections = response.data.map(
            (ps: any) =>
              new PipelineSection(
                ps.pipelineSectionId,
                ps.name,
                ps.diameter,
                ps.trackingFileName,
                ps.trackingFile
              )
          );
          const options = this.pipelineSections
            .map((ps: PipelineSection) => ({
              label: ps.name,
              value: ps.pipelineSectionId,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label));
          this.pipelineSectionOptions = options;
          this.$emit('sections-updated', options);
        })
        .catch((err: any) => {
          console.error('Failed to refresh pipeline sections:', err);
        });
    },
  },
});
</script>

<style scoped>
.manage-ps-dialog .q-tab :deep(.q-tab__label) {
  text-transform: none;
}
</style>
